import {EnvironmentInjector, Injectable, WritableSignal, inject, runInInjectionContext, signal} from '@angular/core';
import {environment} from "../../../environments/environment";
import {ErrorService} from "./error.service";
import {LanguageService} from "./language.service";
import {ToasterService} from './toaster.service';
import {ApiService} from './api.service';
import {TokenService} from './token.service';
import {take} from 'rxjs';
import {TYPE} from '../types/types';
import {Router} from '@angular/router';
import {getMessaging, onMessage, onRegistered, register} from 'firebase/messaging';
import {initializeApp} from 'firebase/app';

@Injectable({
  providedIn: 'root'
})
export class FirebaseService {
  private environmentInjector: EnvironmentInjector = inject(EnvironmentInjector);
  private messaging: ReturnType<typeof getMessaging> | null = null;
  private errorService: ErrorService = inject(ErrorService);
  private languageService: LanguageService = inject(LanguageService);
  private toaster: ToasterService = inject(ToasterService);
  private apiService: ApiService = inject(ApiService);
  private tokenService: TokenService = inject(TokenService);
  private router: Router = inject(Router);

  private token: string | null = null;
  private isInitialized: boolean = false;
  private isForegroundMessageListenerRegistered: boolean = false;
  private isControllerChangeListenerRegistered: boolean = false;
  private isRefreshingForServiceWorkerUpdate: boolean = false;
  private isServiceWorkerActivationRequested: boolean = false;
  private serviceWorkerActivationRequests: WeakSet<ServiceWorker> = new WeakSet<ServiceWorker>();
  private trackedInstallingServiceWorkers: WeakSet<ServiceWorker> = new WeakSet<ServiceWorker>();
  private readonly serviceWorkerReloadGuardStorageKey: string = 'firebase-service-worker-reloaded-at';
  private readonly serviceWorkerReloadGuardDurationMs: number = 2 * 60 * 1000;

  public notificationState: WritableSignal<TYPE.FirebaseNotificationsState> = signal<TYPE.FirebaseNotificationsState>(TYPE.FirebaseNotificationsState.INITIALIZING);

  public getToken(): string | null {
    return this.token;
  }

  public setToken(token: string | null): void {
    this.token = token;
    if (token && this.tokenService.hasToken()) {
      this.apiService.setUserFirebase({
        device_token: token,
        device_type: 'web'
      }).pipe(take(1)).subscribe({
        next: () => this.log('✅ Firebase token registered with backend'),
        error: (error) => console.error('❌ Error registering Firebase token with backend:', error)
      });
    }
  }

  public resetState(): void {
    this.token = null;
    this.messaging = null;
    this.isInitialized = false;
    this.isForegroundMessageListenerRegistered = false;
    this.isControllerChangeListenerRegistered = false;
    this.isRefreshingForServiceWorkerUpdate = false;
    this.isServiceWorkerActivationRequested = false;
    this.serviceWorkerActivationRequests = new WeakSet<ServiceWorker>();
    this.trackedInstallingServiceWorkers = new WeakSet<ServiceWorker>();
    this.notificationState.set(TYPE.FirebaseNotificationsState.INITIALIZING);
  }

  public init(asUserAction: boolean = false): void {
    if (!environment.firebase.active) {
      return;
    }
    if (!this.hasRequiredBrowserMessagingApis()) {
      this.notificationState.set(TYPE.FirebaseNotificationsState.UNSUPPORTED);
      this.log('❌ Browser does not support required Firebase Messaging APIs');
      return;
    }
    const permissionRequest = this.getPermissionRequestForUserAction(asUserAction);
    if (this.isInitialized) {
      if (asUserAction && this.canUseNotificationsApi() && Notification.permission === 'default') {
        void this.checkNotificationPermission(true, permissionRequest);
      }
      return;
    }
    this.isInitialized = true;
    void this.initFirebaseMessaging(asUserAction, permissionRequest);
  }

  private getPermissionRequestForUserAction(asUserAction: boolean): Promise<NotificationPermission> | null {
    if (
      !asUserAction
      || !this.canUseNotificationsApi()
      || Notification.permission !== 'default'
      || !this.requiresDirectPermissionRequestInUserGesture()
    ) {
      return null;
    }

    return Notification.requestPermission();
  }

  private requiresDirectPermissionRequestInUserGesture(): boolean {
    const userAgent = globalThis.navigator?.userAgent?.toLowerCase() ?? '';
    const isFirefox = userAgent.includes('firefox');
    const isSafari = userAgent.includes('safari') && !userAgent.includes('chrome') && !userAgent.includes('chromium') && !userAgent.includes('android');
    return isFirefox || isSafari;
  }

  private async initFirebaseMessaging(
    asUserAction: boolean,
    permissionRequest: Promise<NotificationPermission> | null
  ): Promise<void> {
    try {
      if (!(await this.supportsFirebaseMessaging())) {
        this.notificationState.set(TYPE.FirebaseNotificationsState.UNSUPPORTED);
        this.log('❌ Firebase Messaging is not supported by this browser');
        return;
      }

      this.log('✅ Firebase Messaging is supported by this browser');
      const app = runInInjectionContext(this.environmentInjector, () => initializeApp(environment.firebase.config));
      this.messaging = runInInjectionContext(this.environmentInjector, () => getMessaging(app));
      await this.setupServiceWorker(asUserAction, permissionRequest);
    } catch (error) {
      this.notificationState.set(TYPE.FirebaseNotificationsState.ERROR);
      console.error('❌ Error initializing Firebase Messaging:', error);
    }
  }

  private async setupServiceWorker(
    asUserAction: boolean = false,
    permissionRequest: Promise<NotificationPermission> | null = null
  ): Promise<void> {
    this.log('⚙ Setting up service worker...');
    
    try {
      if ('serviceWorker' in navigator) {
        this.log('✅ Service worker supported');
        const registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js', {scope: '/'});
        this.log('✅ Service worker registered successfully: ' + registration.scope);
        this.setupServiceWorkerImmediateUpdateFlow(registration);
        if (this.isServiceWorkerReloadGuardActive()) {
          this.log('⚙ Recent service worker reload detected, skipping forced update check');
        } else {
          void this.forceServiceWorkerUpdateCheck(registration);
        }
        const readyRegistration = await this.waitForServiceWorkerActivation(registration);
        this.log('✅ Service worker state: ' + readyRegistration.active?.state);
        await this.checkNotificationPermission(asUserAction, permissionRequest);
      } else {
        this.notificationState.set(TYPE.FirebaseNotificationsState.UNSUPPORTED);
        console.error('❌ Service worker not supported');
      }
    } catch (error) {
      this.notificationState.set(TYPE.FirebaseNotificationsState.ERROR);
      console.error('❌ Error setting up service worker:', error);
    }
  }

  private setupServiceWorkerImmediateUpdateFlow(registration: ServiceWorkerRegistration): void {
    this.registerControllerChangeReload();

    if (registration.waiting) {
      this.log('⚙ Service worker update already waiting, requesting immediate activation');
      this.requestImmediateServiceWorkerActivation(registration.waiting);
    }

    registration.addEventListener('updatefound', () => {
      const installingWorker = registration.installing;
      if (!installingWorker) {
        return;
      }

      if (this.trackedInstallingServiceWorkers.has(installingWorker)) {
        return;
      }

      this.trackedInstallingServiceWorkers.add(installingWorker);
      this.log('⚙ Service worker update found');
      installingWorker.addEventListener('statechange', () => {
        this.log('⚙ Installing service worker state: ' + installingWorker.state);

        if (installingWorker.state === 'redundant') {
          return;
        }

        if (installingWorker.state === 'installed' && navigator.serviceWorker.controller) {
          this.log('⚙ Updated service worker installed and waiting, requesting immediate activation');
          this.requestImmediateServiceWorkerActivation(installingWorker);
        }
      });
    });
  }

  private registerControllerChangeReload(): void {
    if (this.isControllerChangeListenerRegistered) {
      return;
    }

    this.isControllerChangeListenerRegistered = true;
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      if (
        !this.isServiceWorkerActivationRequested ||
        this.isRefreshingForServiceWorkerUpdate ||
        this.isServiceWorkerReloadGuardActive()
      ) {
        return;
      }

      this.isRefreshingForServiceWorkerUpdate = true;
      this.storeServiceWorkerReloadGuard();
      this.log('⚙ New service worker took control, reloading page');
      this.reloadForServiceWorkerUpdate();
    });
  }

  private requestImmediateServiceWorkerActivation(worker: ServiceWorker): void {
    if (worker.state === 'redundant') {
      return;
    }

    if (this.isServiceWorkerReloadGuardActive()) {
      this.log('⚙ Recent service worker reload detected, skipping immediate activation request');
      return;
    }

    if (this.serviceWorkerActivationRequests.has(worker)) {
      return;
    }

    this.serviceWorkerActivationRequests.add(worker);
    this.isServiceWorkerActivationRequested = true;
    worker.postMessage('SKIP_WAITING');
  }

  private isServiceWorkerReloadGuardActive(): boolean {
    const lastReloadedAt = this.getLastServiceWorkerReloadedAt();
    return lastReloadedAt !== null && Date.now() - lastReloadedAt < this.serviceWorkerReloadGuardDurationMs;
  }

  private storeServiceWorkerReloadGuard(): void {
    try {
      globalThis.sessionStorage?.setItem(this.serviceWorkerReloadGuardStorageKey, Date.now().toString());
    } catch {
      // Storage can be unavailable in private modes; the in-memory guard still prevents duplicate reloads in this page.
    }
  }

  private getLastServiceWorkerReloadedAt(): number | null {
    try {
      const value = globalThis.sessionStorage?.getItem(this.serviceWorkerReloadGuardStorageKey);
      if (!value) {
        return null;
      }

      const lastReloadedAt = Number(value);
      return Number.isFinite(lastReloadedAt) ? lastReloadedAt : null;
    } catch {
      return null;
    }
  }

  private reloadForServiceWorkerUpdate(): void {
    globalThis.location.reload();
  }

  private async forceServiceWorkerUpdateCheck(registration: ServiceWorkerRegistration): Promise<void> {
    try {
      await registration.update();
      this.log('✅ Forced service worker update check completed');
    } catch (error) {
      console.error('❌ Error forcing service worker update check:', error);
    }
  }

  private async waitForServiceWorkerActivation(registration: ServiceWorkerRegistration): Promise<ServiceWorkerRegistration> {
    const worker = registration.installing || registration.waiting || registration.active;

    if (!worker || worker.state === 'activated') {
      return this.getReadyServiceWorkerRegistration(registration);
    }

    await new Promise<void>((resolve) => {
      const timeout = globalThis.setTimeout(resolve, 5000);

      worker.addEventListener('statechange', () => {
        this.log('⚙ Service worker state: ' + worker.state);

        if (worker.state === 'activated') {
          globalThis.clearTimeout(timeout);
          resolve();
        }
      });
    });

    return this.getReadyServiceWorkerRegistration(registration);
  }

  private async getReadyServiceWorkerRegistration(registration: ServiceWorkerRegistration): Promise<ServiceWorkerRegistration> {
    const readyRegistration = await Promise.race([
      navigator.serviceWorker.ready,
      new Promise<ServiceWorkerRegistration>((resolve) => {
        globalThis.setTimeout(() => resolve(registration), 5000);
      })
    ]);

    if (!readyRegistration.active) {
      throw new Error('Service worker did not activate in time.');
    }

    return readyRegistration;
  }

  private async checkNotificationPermission(
    asUserAction: boolean = false,
    permissionRequest: Promise<NotificationPermission> | null = null
  ): Promise<void> {
    if (!this.canUseNotificationsApi()) {
      this.notificationState.set(TYPE.FirebaseNotificationsState.UNSUPPORTED);
      return;
    }

    let permission = Notification.permission;
    if (permissionRequest) {
      permission = await permissionRequest;
    } else if (permission === 'default' && asUserAction) {
      permission = await Notification.requestPermission();
    }
    if(permission === 'granted') {
      this.log('✅ Notification permission granted');
      this.notificationState.set(TYPE.FirebaseNotificationsState.GRANTED);
      this.setupFirebaseMessaging();
    } else if (permission === 'denied') {
      this.log('❌ Notification permission denied');
      this.notificationState.set(TYPE.FirebaseNotificationsState.DENIED);
    } else {
      this.log('? Notification permission unhandled, permission state: ' + permission);
      if(permission === 'default') {
        this.notificationState.set(TYPE.FirebaseNotificationsState.DEFAULT);
      } else {
        this.notificationState.set(TYPE.FirebaseNotificationsState.UNDEFINED);
      }
    }
  }

  private async setupFirebaseMessaging(): Promise<void> {
    this.log('⚙ Setting up Firebase Messaging...');
    
    try {
      if (!this.messaging) {
        this.notificationState.set(TYPE.FirebaseNotificationsState.UNSUPPORTED);
        this.log('❌ Messaging not initialized');
        return;
      }
      const messaging = this.messaging;
      const serviceWorkerRegistration = await navigator.serviceWorker.ready;
      const firebaseToken = await this.getFirebaseTokenWithRetry(messaging, serviceWorkerRegistration);
      if(firebaseToken) {
        this.log('✅ Firebase token received: ' + firebaseToken);
        this.setToken(firebaseToken);
        this.log('⚙ Setting up Firebase Messaging listener...');
        this.setupForegroundMessageListener(messaging);
      } else {
        this.log('❌ No Firebase token received');
      }
    } catch (error) {
      if (this.isTokenSubscribeFailure(error)) {
        this.log('⚠ Firebase token subscription failed (likely blocked network/privacy settings). A retry will run automatically.');
        globalThis.setTimeout(() => void this.setupFirebaseMessaging(), 30000);
        return;
      }

      console.error('❌ Error setting up Firebase Messaging:', error);
      this.errorService.setError(this.languageService.getTranslationByKey('ERROR'), error, false, false);
    }
  }

  private async getFirebaseTokenWithRetry(
    messaging: ReturnType<typeof getMessaging>,
    serviceWorkerRegistration: ServiceWorkerRegistration
  ): Promise<string | null> {
    const maxAttempts = 3;
    const vapidKey = (environment.firebase as { vapidKey?: string }).vapidKey;

    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      try {
        const firebaseToken = await new Promise<string | null>((resolve, reject) => {
          let settled = false;

          const unsubscribe = runInInjectionContext(this.environmentInjector, () => onRegistered(messaging, (firebaseInstallationId) => {
            if (settled) {
              return;
            }

            settled = true;
            unsubscribe();
            resolve(firebaseInstallationId ?? null);
          }));

          runInInjectionContext(this.environmentInjector, () => register(
            messaging,
            {
              serviceWorkerRegistration,
              vapidKey
            }
          )).catch((error) => {
            if (settled) {
              return;
            }

            settled = true;
            unsubscribe();
            reject(error);
          });
        });
        return firebaseToken ?? null;
      } catch (error) {
        const isRetryableSubscribeFailure = this.isTokenSubscribeFailure(error);
        const isLastAttempt = attempt === maxAttempts;
        if (!isRetryableSubscribeFailure || isLastAttempt) {
          throw error;
        }

        const retryDelay = attempt * 2000;
        this.log(`⚠ Firebase token subscribe attempt ${attempt}/${maxAttempts} failed. Retrying in ${retryDelay}ms.`);
        await new Promise<void>((resolve) => globalThis.setTimeout(resolve, retryDelay));
      }
    }

    return null;
  }

  private isTokenSubscribeFailure(error: unknown): boolean {
    if (!error || typeof error !== 'object') {
      return false;
    }

    const err = error as { code?: string; message?: string };
    const code = err.code || '';
    const message = err.message || '';
    return (
      code.includes('messaging/token-subscribe-failed')
      || code.includes('messaging/fid-registration-failed')
      || message.includes('fcmregistrations.googleapis.com')
    );
  }

  private log(message: string): void {
    if(environment.firebase.setupLogs) {
      console.log('🔥 ' + message);
    }
  }

  private canUseNotificationsApi(): boolean {
    return typeof globalThis.Notification !== 'undefined';
  }

  private hasRequiredBrowserMessagingApis(): boolean {
    return (
      typeof globalThis.window !== 'undefined' &&
      typeof globalThis.navigator !== 'undefined' &&
      typeof globalThis.Notification !== 'undefined' &&
      'serviceWorker' in navigator &&
      typeof globalThis.PushManager !== 'undefined'
    );
  }

  private async supportsFirebaseMessaging(): Promise<boolean> {
    try {
      return await runInInjectionContext(this.environmentInjector, async () => (
        typeof globalThis.window !== 'undefined'
        && typeof globalThis.navigator !== 'undefined'
        && typeof globalThis.Notification !== 'undefined'
        && typeof globalThis.PushManager !== 'undefined'
        && typeof globalThis.ServiceWorkerRegistration !== 'undefined'
        && typeof globalThis.indexedDB !== 'undefined'
        && 'serviceWorker' in navigator
      ));
    } catch {
      return false;
    }
  }

  private setupForegroundMessageListener(messaging: ReturnType<typeof getMessaging>): void {
    if (this.isForegroundMessageListenerRegistered) {
      this.log('⚙ Firebase foreground listener already registered');
      return;
    }

    this.isForegroundMessageListenerRegistered = true;
    runInInjectionContext(this.environmentInjector, () => onMessage(messaging, (payload) => {
      this.log("📩 Firebase Message - Received " + JSON.stringify(payload));
      const appAndMessageInChat = this.router.routerState.snapshot.url.includes('/chat') && payload?.fcmOptions?.link?.includes('type=chat');
      const appAndMessageInCalendar = this.router.routerState.snapshot.url.includes('/calendar') && payload?.fcmOptions?.link?.includes('type=calendar-event');
      if(appAndMessageInChat || appAndMessageInCalendar){
        // here nothing should happen
      } else {
        this.toaster.info(
          this.languageService.getTranslationByKey('NEWNOTIFICATION'),
          this.languageService.getTranslationByKey('CHECKNOTIFICATIONCENTER'),
        );
      }
    }));
    this.log('✅ Firebase foreground listener registered');
  }
}
